import React, { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";

import Button from "../Button/Button";
import FloatingLabelInput from "../FloatingLabelInput/FloatingLabelInput";
import { toastFunction } from "../../utils/toast";
import { backendURL } from "../../utils/backed-url";
import { formatPrice } from "../../utils/format-price";

const MAX_VOUCHERS = 2;
const fieldName = (index) => `pin${index + 1}`;
const lastDigits = (pin) => `•••• ${pin.slice(-4)}`;

// Shops sell OTT vouchers in fixed amounts, so an accessory may take two to
// pay for. Each voucher is checked for its value as it is entered, without
// being redeemed. Only once they add up to the price can the client pay, and
// the backend checks them all again before it redeems either.
const AccessoryVoucherPayment = ({
  accessory,
  clientId,
  onCancel,
  onSuccess,
}) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
    clearErrors,
    resetField,
    getValues,
  } = useForm();

  // The vouchers that passed the check, as { pin, value }, in the order entered.
  const [vouchers, setVouchers] = useState([]);
  const [complete, setComplete] = useState(false);
  const [notice, setNotice] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  // Set when the first voucher was redeemed but the second was not. Trying
  // again would not help, so only Close is offered.
  const [stuck, setStuck] = useState(false);

  const price = Number(accessory.price);
  // Checked vouchers cover this much; nothing is paid until they are redeemed.
  const covered = vouchers.reduce((sum, { value }) => sum + value, 0);
  const remaining = price - covered;
  const nextIndex = vouchers.length;
  const showInput = !complete && nextIndex < MAX_VOUCHERS;

  const failOn = (index, message) => {
    resetField(fieldName(index));
    setError(fieldName(index), { type: "manual", message });
  };

  const checkVoucher = async () => {
    const pin = getValues(fieldName(nextIndex)).trim();
    const pins = [...vouchers.map((voucher) => voucher.pin), pin];

    try {
      const { data } = await axios.post(
        `${backendURL}/v1/api/accessories/voucher/check/`,
        { accessoryId: accessory.id, pins }
      );
      const value = Number(data.vouchers[nextIndex].value);

      if (data.code === "SECOND_VOUCHER_WRONG_AMOUNT") {
        failOn(nextIndex, data.error);
        return;
      }

      setVouchers([...vouchers, { pin, value }]);
      setComplete(data.complete);
      setNotice(data.complete ? "" : data.error);
    } catch (error) {
      const data = error.response?.data;
      // The backend refuses the same voucher twice before checking it.
      const message =
        data?.error ||
        data?.pins?.[0] ||
        "We could not check this voucher, please try again.";
      failOn(nextIndex, message);
    }
  };

  const pay = async () => {
    try {
      await axios.post(`${backendURL}/v1/api/accessories/voucher/`, {
        accessoryId: accessory.id,
        clientId,
        pins: vouchers.map((voucher) => voucher.pin),
      });
      toastFunction(
        `${accessory.name} paid for successfully`,
        "success",
        "voucher-payment-success"
      );
      onSuccess();
    } catch (error) {
      const data = error.response?.data;
      const message = data?.error || "Failed to process voucher payment";
      if (data?.code !== "PARTIALLY_REDEEMED") {
        // Nothing was redeemed, but a voucher may have been used elsewhere
        // since it was checked, so they are entered and checked again.
        startOverFrom(0);
      } else {
        setStuck(true);
      }
      setErrorMessage(message);
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    setErrorMessage("");
    clearErrors();
    try {
      await (complete ? pay() : checkVoucher());
    } finally {
      setLoading(false);
    }
  };

  // Drop this voucher and any entered after it.
  const startOverFrom = (index) => {
    setVouchers(vouchers.slice(0, index));
    setComplete(false);
    setNotice("");
    setErrorMessage("");
    clearErrors();
    for (let i = index; i < MAX_VOUCHERS; i++) {
      resetField(fieldName(i));
    }
  };

  return (
    <div className="basic-modal accessory-voucher">
      <div className="basic-modal__header">
        <h3>Pay for the {accessory.name}</h3>
      </div>
      <div className="basic-modal__body">
        <div className="accessory-voucher__summary">
          <div className="accessory-voucher__amounts">
            <span>
              {vouchers.length
                ? `${formatPrice(covered)} of ${formatPrice(price)} covered`
                : `Price ${formatPrice(price)}`}
            </span>
            {remaining > 0 && <span>{formatPrice(remaining)} to go</span>}
          </div>
          <div
            className="accessory-voucher__progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={price}
            aria-valuenow={covered}
          >
            <div
              className="accessory-voucher__progress-fill"
              style={{ width: `${Math.min(100, (covered / price) * 100)}%` }}
            />
          </div>
        </div>

        {errorMessage && (
          <div className="input-container__error" role="alert">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="basic-modal__form">
          {vouchers.length > 0 && (
            <ul className="accessory-voucher__list">
              {vouchers.map((voucher, index) => (
                <li key={voucher.pin} className="accessory-voucher__item">
                  <span className="accessory-voucher__check" aria-hidden="true">
                    ✓
                  </span>
                  <span className="accessory-voucher__label">
                    Voucher {index + 1}
                    <small>{lastDigits(voucher.pin)}</small>
                  </span>
                  <span className="accessory-voucher__value">
                    {formatPrice(voucher.value)}
                  </span>
                  {!stuck && (
                    <button
                      type="button"
                      className="accessory-voucher__remove"
                      onClick={() => startOverFrom(index)}
                      disabled={loading}
                      aria-label={`Remove voucher ${index + 1}`}
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {notice && (
            <p className="accessory-voucher__notice" role="status">
              {notice}
            </p>
          )}

          {showInput && !stuck && (
            <FloatingLabelInput
              key={fieldName(nextIndex)}
              type={"text"}
              name={fieldName(nextIndex)}
              register={register}
              rules={{ required: "PIN is required" }}
              placeholder={
                nextIndex === 0
                  ? "Enter Voucher PIN"
                  : `Second voucher PIN (${formatPrice(remaining)})`
              }
              errors={errors}
              autoComplete={"off"}
            />
          )}

          {complete && vouchers.length > 1 && !stuck && (
            <p className="accessory-voucher__notice" role="status">
              Both vouchers are valid. They will only be used once you pay.
            </p>
          )}

          <div className="basic-modal__footer">
            <button
              type="button"
              onClick={onCancel}
              className="button button--secondary"
            >
              {stuck ? "Close" : "Cancel"}
            </button>
            {!stuck && (
              <Button
                type={"submit"}
                loading={loading}
                name={complete ? `Pay ${formatPrice(price)}` : "Check Voucher"}
                className={"button--primary"}
              />
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccessoryVoucherPayment;
